import { Fragment, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useProperty } from '@vibesites/runtime/react';
import { getEligibleNavPages, hasInteractiveMapContent } from './_navShared';

const SITEMAP_EXCLUDED_ALIASES = new Set<string>(['apply-now', 'resident-access']);

export function SitemapContent() {
  // Mirror the header's "pages in navigation" set exactly (see ./_navShared),
  // so the sitemap lists the same pages the visitor can reach from the nav.
  // Virtual Tours is always listed here, whether or not the property has tour
  // data — the page's own visibility setting is the only gate.
  const property = useProperty() as Record<string, unknown>;

  // Same Interactive Map gate as the header, so the sitemap doesn't surface a
  // page the nav hides. (The override token is header-scoped and read-only here.)
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveLeasing = hasInteractiveMapContent(property, interactiveMapShowIfExists);

  const pages = useMemo(
    () =>
      getEligibleNavPages(true, hasInteractiveLeasing)
        .filter(([alias]) => !SITEMAP_EXCLUDED_ALIASES.has(alias))
        .map(([, page]) => page),
    [hasInteractiveLeasing],
  );

  return (
    <section className="py-12 md:py-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {pages.map((page, index) => {
          // Trim the space above the "Floor Plans" entry by 30px.
          const isFloorPlans = page.name.trim().toLowerCase() === 'floor plans';
          return (
            <Fragment key={page.path}>
              {index > 0 && (
                <hr className="mx-auto w-12 border-t border-[var(--vibe-color-border)]" />
              )}
              <a
                href={page.path}
                className={`block text-center font-heading text-lg md:text-2xl font-normal text-[var(--vibe-color-primary)] py-3 hover:opacity-80 transition-opacity${
                  isFloorPlans ? ' -mt-[30px]' : ''
                }`}
              >
                {page.name}
              </a>
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
